import React, { useEffect, useRef, useState } from 'react';
import { isNil } from 'lodash';
import { cn } from '@/lib/utils';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  type AxisFormat,
  axisFormatFromDataType,
  formatDisplayUnitNumber,
  formatDisplayUnitValue,
  isPercent,
  NumberInput,
} from 'venn-components';
import {
  blockScatterAxisConfig,
  blockScatterAxisRenderedExtremes,
  blockScatterAxisStaticHints,
  type BlockId,
  type ScatterAxisConfig,
  type ScatterAxisRange,
  type ScatterAxisStaticHints,
} from 'venn-state';
import { Label } from 'venn-ui-kit';
import { Numbers } from 'venn-utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContentWithArrow, TooltipTrigger } from '@/components/ui/tooltip';

const appendPercentSuffix = (value: string) => (value === '' ? value : `${value}%`);

// Display-only formatting for the input value, shared with the placeholder path (formatDisplayUnitValue
// via formatDisplayUnitNumber) so a filled field and its placeholder render the same number identically.
// The committed value stays exact — the % suffix, when needed, is added by NumberInput's formatter.
const formatAxisInputValue = (value: number): string => formatDisplayUnitNumber(value);

interface ScatterAxisSectionProps {
  selectedBlockId: BlockId;
  isReadOnly?: boolean;
}

const NULL_RANGE: ScatterAxisRange = { min: null, max: null, interval: null };

type AxisFieldProps = {
  label: string;
  value: number | null;
  disabled?: boolean;
  placeholder?: string;
  focusPlaceholder?: string;
  /** When the value is null and +/- is clicked, step from this base instead of 0. */
  nullStepBase?: number;
  clipsData?: boolean;
  format: AxisFormat;
  onChange: (value: number | null) => void;
  /** Monotonically increasing tick. When it changes, the field briefly flashes to
   * indicate that the value was auto-adjusted (e.g. snapped to keep Min ≤ Max). */
  flashTick?: number;
};

const CLIPS_DATA_TOOLTIP = 'One or more data points lie outside the bounds of this value.';

const AxisField = ({
  label,
  value,
  disabled,
  placeholder,
  focusPlaceholder,
  nullStepBase,
  clipsData,
  format,
  onChange,
  flashTick,
}: AxisFieldProps) => {
  const [focused, setFocused] = useState(false);
  const showClipWarning = Boolean(clipsData);
  const activePlaceholder = focused && focusPlaceholder ? focusPlaceholder : (placeholder ?? 'Auto');
  const stepBase = value ?? nullStepBase;

  // State-driven flash (replaces an imperative classList toggle + forced reflow): a flashTick
  // change arms the animation class; onAnimationEnd disarms it so the next tick can replay it.
  // No remount, so the field — which is the focused one when its own edit is rejected — keeps focus.
  const [flashing, setFlashing] = useState(false);
  useEffect(() => {
    if (!flashTick) {
      return undefined;
    }

    // Drop the class for one frame so a tick arriving mid-animation restarts it
    // (setFlashing(true) alone is a no-op while already flashing).
    setFlashing(false);
    const id = requestAnimationFrame(() => setFlashing(true));

    return () => {
      cancelAnimationFrame(id);
    };
  }, [flashTick]);

  return (
    <div className="flex flex-col gap-1">
      <Label>{label}</Label>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            data-testid="axis-field-flash"
            className={cn('rounded-md', flashing && 'animate-flash-highlight')}
            onAnimationEnd={() => setFlashing(false)}
          >
            <NumberInput
              label={label}
              value={value}
              disabled={disabled}
              placeholder={activePlaceholder}
              step={10 ** -Numbers.precisionFor(stepBase)}
              nullStepBase={nullStepBase}
              width={60}
              autoFitFont
              allowNull
              controlled
              resetTick={flashTick}
              displayFormatter={formatAxisInputValue}
              formatter={isPercent(format) ? appendPercentSuffix : undefined}
              error={showClipWarning}
              onChange={onChange}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
            />
          </div>
        </TooltipTrigger>
        {showClipWarning && <TooltipContentWithArrow>{CLIPS_DATA_TOOLTIP}</TooltipContentWithArrow>}
      </Tooltip>
    </div>
  );
};

const formatRenderedValue = (value: number | undefined, format: AxisFormat): string | undefined =>
  isNil(value) || Number.isNaN(value) ? undefined : formatDisplayUnitValue(value, format);

const collapseEmptyConfig = (config: ScatterAxisConfig): ScatterAxisConfig | undefined => {
  const { x, y } = config;
  if (x.min == null && x.max == null && x.interval == null && y.min == null && y.max == null && y.interval == null) {
    return undefined;
  }

  return config;
};

// Apply an edit to one bound, rejecting it (returning prev unchanged) when it would break Min ≤ Max.
// Reference-equal return signals rejection to the caller, which bounces the field back and flashes it.
const applyAxisEdit = (
  prev: ScatterAxisRange,
  field: keyof ScatterAxisRange,
  value: number | null,
): ScatterAxisRange => {
  if (field === 'min' && value != null && !isNil(prev.max) && value > prev.max) {
    return prev;
  }

  if (field === 'max' && value != null && !isNil(prev.min) && value < prev.min) {
    return prev;
  }

  return { ...prev, [field]: value };
};

const clipsMin = (m: ScatterAxisStaticHints | undefined) => m?.clipsData === 'min' || m?.clipsData === 'both';
const clipsMax = (m: ScatterAxisStaticHints | undefined) => m?.clipsData === 'max' || m?.clipsData === 'both';

export const ScatterAxisSection = ({ selectedBlockId, isReadOnly }: ScatterAxisSectionProps) => {
  const [config, setConfig] = useRecoilState(blockScatterAxisConfig(selectedBlockId));
  const staticHints = useRecoilValue(blockScatterAxisStaticHints(selectedBlockId));
  const renderedExtremes = useRecoilValue(blockScatterAxisRenderedExtremes(selectedBlockId));

  const xFormat = axisFormatFromDataType(staticHints?.x?.dataType);
  const yFormat = axisFormatFromDataType(staticHints?.y?.dataType);

  const x = config?.x ?? NULL_RANGE;
  const y = config?.y ?? NULL_RANGE;

  const hasValues = config !== undefined;

  // Per-field flash tick. Bumped whenever the field's value was auto-adjusted by the
  // counterpart edit (e.g. user pushed Min above Max — Max snaps up and flashes).
  const [flashTicks, setFlashTicks] = useState<{
    x: { min: number; max: number };
    y: { min: number; max: number };
  }>({ x: { min: 0, max: 0 }, y: { min: 0, max: 0 } });
  const [snapAnnouncement, setSnapAnnouncement] = useState('');

  // Mirror the latest config in a ref so updateAxis always reads fresh state — rapid successive
  // edits (e.g. stepper clicks) would otherwise compute the snap decision off a stale closure.
  const configRef = useRef(config);
  configRef.current = config;

  const updateAxis = (axis: 'x' | 'y', field: keyof ScatterAxisRange, value: number | null) => {
    const prevConfig = configRef.current;
    const prevAxis = prevConfig?.[axis] ?? NULL_RANGE;
    if (prevAxis[field] === value) {
      return;
    }

    const nextAxis = applyAxisEdit(prevAxis, field, value);

    // Edit rejected (would break Min ≤ Max): leave config untouched. The field bounces back to its
    // previous value (via resetTick) and flashes to signal the rejection.
    if (nextAxis === prevAxis) {
      if (field === 'min' || field === 'max') {
        setFlashTicks((ticks) => ({
          ...ticks,
          [axis]: { ...ticks[axis], [field]: ticks[axis][field] + 1 },
        }));
        const constraint = field === 'min' ? 'cannot be greater than Max' : 'cannot be less than Min';
        setSnapAnnouncement(`${axis.toUpperCase()} ${field === 'min' ? 'Min' : 'Max'} ${constraint}`);
      }
      return;
    }

    const otherAxis = axis === 'x' ? (prevConfig?.y ?? NULL_RANGE) : (prevConfig?.x ?? NULL_RANGE);
    const nextConfig = axis === 'x' ? { x: nextAxis, y: otherAxis } : { x: otherAxis, y: nextAxis };
    setConfig(collapseEmptyConfig(nextConfig));
  };

  return (
    <div className="mt-[8px] flex flex-col gap-[8px] relative">
      <span className="sr-only" aria-live="polite">
        {snapAnnouncement}
      </span>
      {isReadOnly ? undefined : (
        <div className="absolute bottom-full right-0 mb-[8px]">
          <Button
            variant="outline"
            size="sm"
            disabled={!hasValues}
            className="border-venn-grey-30"
            onClick={() => setConfig(undefined)}
          >
            Reset values
          </Button>
        </div>
      )}

      <div className="flex gap-x-[8px]">
        <span className="mb-[5px] flex items-end text-[12px] font-bold">X</span>
        <AxisField
          label="Min"
          value={x.min}
          disabled={isReadOnly}
          placeholder={staticHints?.x?.zeroBaseline === 'min' ? formatRenderedValue(0, xFormat) : undefined}
          focusPlaceholder={
            staticHints?.x?.zeroBaseline === 'min' ? undefined : formatRenderedValue(renderedExtremes?.x?.min, xFormat)
          }
          nullStepBase={staticHints?.x?.zeroBaseline === 'min' ? undefined : renderedExtremes?.x?.min}
          clipsData={clipsMin(staticHints?.x)}
          format={xFormat}
          onChange={(v) => updateAxis('x', 'min', v)}
          flashTick={flashTicks.x.min}
        />
        <AxisField
          label="Max"
          value={x.max}
          disabled={isReadOnly}
          placeholder={staticHints?.x?.zeroBaseline === 'max' ? formatRenderedValue(0, xFormat) : undefined}
          focusPlaceholder={
            staticHints?.x?.zeroBaseline === 'max' ? undefined : formatRenderedValue(renderedExtremes?.x?.max, xFormat)
          }
          nullStepBase={staticHints?.x?.zeroBaseline === 'max' ? undefined : renderedExtremes?.x?.max}
          clipsData={clipsMax(staticHints?.x)}
          format={xFormat}
          onChange={(v) => updateAxis('x', 'max', v)}
          flashTick={flashTicks.x.max}
        />
        <AxisField
          label="Interval"
          value={x.interval}
          disabled={isReadOnly}
          focusPlaceholder={formatRenderedValue(renderedExtremes?.x?.interval, xFormat)}
          nullStepBase={renderedExtremes?.x?.interval}
          format={xFormat}
          onChange={(v) => updateAxis('x', 'interval', v)}
        />
      </div>

      <div className="flex gap-x-[8px]">
        <span className="mb-[5px] flex items-end text-[12px] font-bold">Y</span>
        <AxisField
          label="Min"
          value={y.min}
          disabled={isReadOnly}
          placeholder={staticHints?.y?.zeroBaseline === 'min' ? formatRenderedValue(0, yFormat) : undefined}
          focusPlaceholder={
            staticHints?.y?.zeroBaseline === 'min' ? undefined : formatRenderedValue(renderedExtremes?.y?.min, yFormat)
          }
          nullStepBase={staticHints?.y?.zeroBaseline === 'min' ? undefined : renderedExtremes?.y?.min}
          clipsData={clipsMin(staticHints?.y)}
          format={yFormat}
          onChange={(v) => updateAxis('y', 'min', v)}
          flashTick={flashTicks.y.min}
        />
        <AxisField
          label="Max"
          value={y.max}
          disabled={isReadOnly}
          placeholder={staticHints?.y?.zeroBaseline === 'max' ? formatRenderedValue(0, yFormat) : undefined}
          focusPlaceholder={
            staticHints?.y?.zeroBaseline === 'max' ? undefined : formatRenderedValue(renderedExtremes?.y?.max, yFormat)
          }
          nullStepBase={staticHints?.y?.zeroBaseline === 'max' ? undefined : renderedExtremes?.y?.max}
          clipsData={clipsMax(staticHints?.y)}
          format={yFormat}
          onChange={(v) => updateAxis('y', 'max', v)}
          flashTick={flashTicks.y.max}
        />
        <AxisField
          label="Interval"
          value={y.interval}
          disabled={isReadOnly}
          focusPlaceholder={formatRenderedValue(renderedExtremes?.y?.interval, yFormat)}
          nullStepBase={renderedExtremes?.y?.interval}
          format={yFormat}
          onChange={(v) => updateAxis('y', 'interval', v)}
        />
      </div>
    </div>
  );
};
